import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

/**
 * A cell renderer param that can either be a static value or a function derived
 * from the row's data, letting consumers configure the button per row.
 */
export type ButtonCellValue<TData, TValue> = TValue | ((data: TData) => TValue);

/**
 * Params for {@link ButtonCellComponent}. Every input of `app-button` is
 * configurable, either as a static value or as a function of the row data.
 */
export interface ButtonCellParams<TData = unknown> extends ICellRendererParams<TData> {
    onClick: (data: TData) => void;
    type?: ButtonCellValue<TData, 'icon' | 'stroked' | 'flat' | 'text'>;
    color?: ButtonCellValue<TData, 'primary' | 'danger'>;
    icon?: ButtonCellValue<TData, string>;
    iconPos?: ButtonCellValue<TData, 'before' | 'after'>;
    buttonText?: ButtonCellValue<TData, string>;
    disabled?: ButtonCellValue<TData, boolean>;
    isLoading?: ButtonCellValue<TData, boolean>;
    highlight?: ButtonCellValue<TData, boolean>;
    tooltip?: ButtonCellValue<TData, string>;
}

/**
 * Generic ag-grid cell renderer that wraps `app-button`. Consumers supply an
 * `onClick` handler and any `app-button` inputs they need via
 * `cellRendererParams`, each as a static value or a function of the row data.
 */
@Component({
    selector: 'app-button-cell',
    templateUrl: './button-cell.component.html',
    styleUrls: ['./button-cell.component.scss'],
    standalone: false,
})
export class ButtonCellComponent<TData = unknown> implements ICellRendererAngularComp {
    data!: TData;
    private params!: ButtonCellParams<TData>;

    agInit(params: ButtonCellParams<TData>): void {
        this.params = params;
        this.data = params.data as TData;
    }

    refresh(params: ButtonCellParams<TData>): boolean {
        this.params = params;
        this.data = params.data as TData;
        return true;
    }

    private resolve<TValue>(value: ButtonCellValue<TData, TValue> | undefined): TValue | undefined {
        return typeof value === 'function' ? (value as (data: TData) => TValue)(this.data) : value;
    }

    get type(): 'icon' | 'stroked' | 'flat' | 'text' {
        return this.resolve(this.params?.type) ?? 'stroked';
    }

    get color(): 'primary' | 'danger' {
        return this.resolve(this.params?.color) ?? 'primary';
    }

    get icon(): string | undefined {
        return this.resolve(this.params?.icon);
    }

    get iconPos(): 'before' | 'after' {
        return this.resolve(this.params?.iconPos) ?? 'before';
    }

    get buttonText(): string | undefined {
        return this.resolve(this.params?.buttonText);
    }

    get disabled(): boolean {
        return this.resolve(this.params?.disabled) ?? false;
    }

    get isLoading(): boolean {
        return this.resolve(this.params?.isLoading) ?? false;
    }

    get highlight(): boolean {
        return this.resolve(this.params?.highlight) ?? false;
    }

    get tooltip(): string {
        return this.resolve(this.params?.tooltip) ?? '';
    }

    buttonClick(): void {
        if (!this.disabled) {
            this.params?.onClick(this.data);
        }
    }
}
